/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import dayjs from "dayjs";

// Utils
import { useInputContext } from "src/contexts/InputContext";
import { transformCombiData } from "src/utils/functions/chartDataConversion/transformCombiData";
import { getInputsToPrompt } from "src/utils/functions/promptConversion/getInputsToPrompt";

// Queries
import { usePostFuture } from "src/queries";

// Components
import MainPage from "./MainPage";
import { PageBar } from "src/components/atoms/PageBar";
import { CashFlowData } from "src/utils/types";
import { getDateFromArray } from "src/utils/functions";
import { chooseGranularity } from "src/utils/functions/promptConversion/promptConversionHelpers";

const DataWrapper = () => {
  const { inputItems, portfolioData } = useInputContext();
  const defaultPrompt = getInputsToPrompt({ portfolioData, data: inputItems });
  const [prompt, setPrompt] = useState(defaultPrompt);

  const [forecastData, setForecastData] = useState<any>({
    data: undefined,
    error: "",
    raw: undefined,
  });

  const currentDate = dayjs(new Date()).format("YYYY-MM-DD");
  const defaultFuture = dayjs(new Date()).add(1, "year").format("YYYY-MM-DD");
  const [timeRange, setTimeRange] = useState<{
    from: undefined | string;
    to: undefined | string;
    granularity: undefined | number;
  }>({
    from: currentDate,
    to: defaultFuture,
    granularity: undefined,
  });

  const postForecast = usePostFuture({
    onSuccess: (response: any) => {
      if (response.data) {
        const transformedData = transformCombiData({
          data: response.data,
          input: inputItems,
        });
        setForecastData({ ...transformedData, raw: response.data });
      }
    },
    onError: (response: any) => {
      if (response.response.data) {
        setForecastData({
          data: undefined,
          error: `${response.response.data.title} details: ${response.response.data.detail}`,
        });
      }
    },
  });

  useEffect(() => {
    const newPrompt = getInputsToPrompt({ portfolioData, data: inputItems });

    const dateArray =
      newPrompt.portfolio.services.flatMap((service) =>
        service.products.flatMap((product) =>
          product.cashFlow.flatMap(
            (cashFlow: CashFlowData) => cashFlow?.from || cashFlow?.date
          )
        )
      ) || [];
    const futureDateArray =
      newPrompt.portfolio.services.flatMap((service) =>
        service.products.flatMap((product) =>
          product.cashFlow.flatMap(
            (cashFlow: CashFlowData) =>
              cashFlow?.to || cashFlow?.from || cashFlow?.date
          )
        )
      ) || [];

    const oldestDate = getDateFromArray({
      dateArr: [...dateArray, timeRange.from],
      direction: "oldest",
    });
    const newestDate = getDateFromArray({
      dateArr: [...futureDateArray, timeRange.to],
      direction: "newest",
    });

    const defaultGranularity = chooseGranularity({
      startDate: oldestDate,
      endDate: newestDate,
    });

    setTimeRange({
      granularity: defaultGranularity || 9,
      to: newestDate,
      from: oldestDate,
    });
    setPrompt(newPrompt);

    if (inputItems.length === 0) {
      setForecastData({
        data: undefined,
        error: "",
      });
    }
  }, [inputItems, portfolioData]);

  const timeCondition =
    dayjs(timeRange.from).toDate().getTime() >
      dayjs("1986-01-31").toDate().getTime() &&
    dayjs(timeRange.to).toDate().getTime() >
      dayjs("1986-01-31").toDate().getTime();

  useEffect(() => {
    if (prompt?.portfolio?.services?.length > 0 && timeCondition) {
      postForecast.mutate({
        ...prompt,
        from: timeRange.from,
        forecastFrom: currentDate,
        to: timeRange.to,
        granularity: timeRange.granularity || 9,
      });
    }
  }, [prompt, timeRange]);

  return (
    <div className="App">
      <PageBar />

      <MainPage
        forecastData={forecastData}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        isFetching={postForecast.isLoading}
      />
    </div>
  );
};

export default DataWrapper;
